import {h, Component} from 'preact';
import invoke from 'lodash.invoke';
import __ from 'ramda/src/__';
import and from 'ramda/src/and';
import map from 'ramda/src/map';
import prop from 'ramda/src/prop';
import gte from 'ramda/src/gte';
import compose from 'ramda/src/compose';
import length from 'ramda/src/length';
import flatten from 'ramda/src/flatten';
import pick from 'ramda/src/pick';
import deepEqual from 'deep-equal';
import Provider from 'preact-context-provider';
import {createSearchContext} from '@ultradent/upi-fuzzy-search';
import SearchInput from '../SearchInput';
import SearchResults from '../SearchResults';
import SearchResultsPane from '../SearchResultsPane';
import SearchResultItem from '../SearchResultItem';
import shallowCompare from 'preact-shallow-compare';
import mediaQuery from '@util/media-queries';

function withSearch( Component ) {
    function Wrapper( props, context ) {
        return (
            <Provider {...context}>
                <Component search={{...context}} {...props} />
            </Provider>
        );
    }

    Wrapper.displayName = `withSearch(${Component.displayName || Component.name})`;
    return Wrapper;
}

class Search extends Component {

    static ResultsPane = SearchResultsPane;
    static ResultItem = SearchResultItem;
    static Input = withSearch( SearchInput );
    static Results = withSearch( SearchResults );

    resultGroups = [];
    // start with empty list which gets updated by onResultChange events from SearchResultPagination groups
    visibleResultGroups = [];

    static defaultProps = {
        initialQuery: '',
        alwaysShowResults: false,
        hideOnFocusout: false,
        onResultSelect: () => {
        },
        queryGroup: {
            queryProps: [],
            list: []
        },
        resultLimit: 7
    };

    constructor( props ) {
        super( props );

        const constructedQueryGroups = this.constructQueryGroups( props );

        this.state = {
            selectedIndex: -1,
            isFocused: false,
            hideOnFocusout: props.hideOnFocusout,
            constructedQueryGroups,
            query: props.initialQuery || ''
        }
    }

    componentDidMount() {
        if ( this.state.hideOnFocusout ) {
            document.addEventListener( 'click', e => this.hideOnFocusout( e.target ) );
        }
    }

    shouldComponentUpdate( nextProps, nextState ) {
        return shallowCompare( this, nextProps, nextState );
    }

    componentWillReceiveProps( nextProps ) {
        if ( !deepEqual( nextProps.queryGroup !== this.props.queryGroup ) ) {
            const constructedQueryGroups = this.constructQueryGroups( nextProps );
            this.setState( {constructedQueryGroups} );
        }
    }

    hideOnFocusout( target ) {
        const breakpointName = mediaQuery.getBreakPointAlias();
        if ( breakpointName === 'small' ) {
            return;
        }
        // click occurred inside the search component or a page modal -> remain active
        while (
            target
            && target !== this.wrapper
            && target.nodeName.toLowerCase() !== 'html' ) {
            target = target.parentNode;
        }
        if ( !target || (target && target.nodeName.toLowerCase() === 'html') ) {
            this.setState( {isFocused: false} );
        }
    }

// [[{results: a}],[{results: b}]] -> Array [a,b]
    flattenResultGroups = compose( flatten, map( prop( 'results' ) ) );
// [[{results: a}],[{results: b}]] -> Int
    getResultCount = compose( length, this.flattenResultGroups );
// [[a],[b]] -> Boolean
    hasResults = compose( gte( __, 1 ), this.getResultCount );

    constructQueryGroups( props ) {
        const {queryGroup} = props;
        const queryGroups = Array.isArray( queryGroup ) ? queryGroup : [queryGroup];

        // create a list of searchContexts each with their own data sets that can later be queried
        return queryGroups.map( grp => {
            const {list, queryProps} = grp;
            const ctx = createSearchContext( list, queryProps );

            if ( grp.filter ) {
                ctx.setFilters( grp.filter );
            }

            return {...grp, query: ctx.query};
        } );
    }

    getQueriedResults = query => {
        const defaultResultLimit = this.props.resultLimit;

        return this.state.constructedQueryGroups.map( ctx => {
            let {results} = ctx.query( query );
            // apply index & metadata to each result
            return {
                results,
                resultLimit: ctx.resultLimit || defaultResultLimit
            };
        } )
    };

    getActiveState = ( query, isFocused ) => and( gte( query.length, 3 ), isFocused );

    setSelectionIndex = ( index ) => {
        // force index boundaries from 0 - maxLength
        const visibleCount = this.getResultCount( this.visibleResultGroups );
        const selectedIndex = Math.min( Math.max( index, -1 ), visibleCount - 1 );
        this.setState( {selectedIndex} );
    };

    onVisibleResultsChange = ( index, newResults ) => {
        this.visibleResultGroups[index] = newResults;
    };

    onEnter = ( evt ) => {
        evt.preventDefault();
        const index = this.state.selectedIndex;
        if ( index === -1 ) {
            return;
        }

        const flatResults = this.flattenResultGroups( this.visibleResultGroups );
        this.handleResultSelect( evt, flatResults[index] );
    };

    handleResultClick = ( evt, {index} ) => {
        const flatResults = this.flattenResultGroups( this.visibleResultGroups );
        this.handleResultSelect( evt, flatResults[index] );
    };

    handleResultHover = ( evt, {index} ) => {
        this.setSelectionIndex( index );
    };

    handleResultSelect = ( evt, result ) => {
        invoke( this.props, 'onResultSelect', evt, result );
    };

    onInput = value => {
        this.onSearch( value );
    };

    onClear = () => {
        this.setState( {query: ''} );
    };

    onFocus = () => {
        this.setState( {isFocused: true} );
        this.onSearch( this.state.query );
    };

    onKeyUp = keycode => {
        this.setState( {isFocused: keycode !== 27} );
    };

    onKeyDown = keycode => {
        switch ( keycode ) {
            case 38:
                this.setSelectionIndex( this.state.selectedIndex - 1 );
                break;
            case 40:
                this.setSelectionIndex( this.state.selectedIndex + 1 );
                break;

            default:
            // fall through
        }
    };

    onSearch = query => {
        // reset selectedIndex anytime a query changes
        this.setSelectionIndex( -1 );
        this.setState( {query} );
    };

    render( {alwaysShowResults, showPagination, children} ) {
        const {query, isFocused, constructedQueryGroups} = this.state;

        const eventHandlers = pick( [
            'onClear',
            'onInput',
            'onEnter',
            'onKeyDown',
            'onKeyUp',
            'onFocus',
            'onVisibleResultsChange',
            'handleResultHover',
            'handleResultClick'], this );

        const isActive = this.getActiveState( query, isFocused );
        this.resultGroups = (isActive || alwaysShowResults) ? this.getQueriedResults( query ) : [];

        const sendProps = {
            hasResults: this.hasResults( this.resultGroups ),
            resultGroups: this.resultGroups,
            queryGroups: constructedQueryGroups,
            isActive,
            showPagination,
            alwaysShowResults,
            ...eventHandlers,
            ...this.state
        };

        return (
            <Provider {...sendProps}>
                <div
                    ref={ref => this.wrapper = ref}
                    data-testid="search-container"
                    className="w-full">
                    {children[0]( {...sendProps} )}
                </div>
            </Provider>
        );
    }
}

export default Search;



